import { useIsMobile, WSFlexBox } from "@wingspanhq/fe-component-library";
import * as React from "react";
import { useFocusedFeatures } from "../../../FocusedFeatures";
import { useCustomization } from "../../../modules/customization";
import { useQueryNotifications } from "../../../query/notifications/queries/useQueryNotifications";
import { selectorCountOfVisibleNotifications } from "../../../shared/selectors/selectorCountOfVisibleNotifications";
import { plural } from "../../../shared/utils/plural";
import { CollapsibleMenu } from "../CollapsibleMenu";
import { New } from "../New";
import { SideNavMenuItem } from "../SideNavMenuItem";
import { SideNavRootLinkOption } from "../SideNavRootLinkOption";
import styles from "../styles.module.scss";

import {
  PATH_BENEFITS_AXIS_INSURANCE,
  PATH_BENEFITS_INSURANCE,
  PATH_BENEFITS_PERKS
} from "../../../modules/Benefits/paths";
import { ENGAGEMENT_TYPES_ROOT_PATH } from "../../../modules/EngagementTypes/paths";
import { ENGAGEMENT_REQUIREMENTS_ROOT_PATH } from "../../../modules/RequirementDefinitions/paths/constants";
import { PATH_TAXES } from "../../../modules/Taxes/paths";
import {
  PATH_WORK_LOGS_PAYEE,
  PATH_WORK_LOGS_PAYER
} from "../../../modules/WorkLogs/paths";
import { selectorCanEnableTaxWithholdings } from "../../../modules/Taxes/selectors/selectorCanEnableTaxWithholdings";
import { TAX_FILING_1099_FILING_PATH } from "../../../modules/TaxFiling/paths";
import { useUserId } from "../../../query/hooks/helpers";
import { useQueryCustomerEntity } from "../../../query/onboarding/queries/useQueryCustomerEntity";
import { useQueryMemberProfile } from "../../../query/users/queries";

export enum ExpandableSideNavMenuOptions {
  GetPaid = "GetPaid",
  SendPayments = "SendPayments",
  FinancialBenefits = "FinancialBenefits",
  LifestyleBenefits = "LifestyleBenefits",
  Reporting = "Reporting",
  TaxFiling = "TaxFilings",
  DataAndIntegrations = "DataAndIntegrations",
  Settings = "Settings"
}

export type DefaultNavProps = {
  onExpand: (value: ExpandableSideNavMenuOptions) => void;
  isSideNavOpened: boolean;
  setIsDrawerOpened: (v: boolean) => void;
  setIsSideNavOpened: (v: boolean) => void;
  expandedItems: ExpandableSideNavMenuOptions[];
};

export const DefaultNav: React.FC<DefaultNavProps> = ({
  onExpand,
  expandedItems,
  setIsDrawerOpened,
  isSideNavOpened,
  setIsSideNavOpened
}) => {
  const isMobile = useIsMobile();
  const focusedFeatures = useFocusedFeatures();
  const queryNotifications = useQueryNotifications();
  const queryMonitoredEntity = useQueryCustomerEntity();
  const userId = useUserId();
  const queryMember = useQueryMemberProfile(userId);
  const { terminology } = useCustomization();

  return (
    <>
      {focusedFeatures.showSideNavNew && (
        <New
          isSideNavOpened={isSideNavOpened}
          setIsSideNavOpened={isMobile ? setIsDrawerOpened : setIsSideNavOpened}
        />
      )}

      <SideNavRootLinkOption
        isVisible={focusedFeatures.showSideNavGettingStarted}
        to="/member/dashboard"
        testId="memberGettingStarted"
        label="Home"
        icon="home"
        isSideNavOpened={isSideNavOpened}
        count={
          queryNotifications.data &&
          selectorCountOfVisibleNotifications(queryNotifications.data)
        }
      />

      <WSFlexBox direction="column" className={styles.topNavItems} mt="M">
        <CollapsibleMenu
          isVisible={focusedFeatures.showSideNavGetPaid}
          isSideNavOpened={isSideNavOpened}
          icon="in"
          label="Get paid"
          isExpanded={expandedItems.includes(
            ExpandableSideNavMenuOptions.GetPaid
          )}
          onClick={() => onExpand(ExpandableSideNavMenuOptions.GetPaid)}
        >
          <SideNavMenuItem
            linkTo="/member/invoices"
            testId="paymentsInvoicing"
            isVisible={true}
            label={plural(terminology().getPaidInvoice)}
            exact
          />

          <SideNavMenuItem
            linkTo={PATH_WORK_LOGS_PAYEE}
            testId="workLogsPayee"
            isVisible={focusedFeatures.showSideNavWorkLogsPayee}
            label="Work logs"
          />

          <SideNavMenuItem
            linkTo="/member/payers"
            testId="clients"
            isVisible={true}
            label={plural(terminology().getPaidClient)}
          />
        </CollapsibleMenu>

        <CollapsibleMenu
          isVisible={focusedFeatures.showSideNavSendPayments}
          isSideNavOpened={isSideNavOpened}
          icon="out"
          label="Send payments"
          isExpanded={expandedItems.includes(
            ExpandableSideNavMenuOptions.SendPayments
          )}
          onClick={() => onExpand(ExpandableSideNavMenuOptions.SendPayments)}
        >
          <SideNavMenuItem
            linkTo="/member/invoices/payables"
            testId="paymentsPayables"
            isVisible={focusedFeatures.showSideNavPayables}
            label={plural(terminology().sendPaymentsPayable)}
          />

          <SideNavMenuItem
            linkTo={PATH_WORK_LOGS_PAYER}
            testId="workLogsPayer"
            isVisible={focusedFeatures.showSideNavWorkLogsPayer}
            label="Work logs"
          />

          <SideNavMenuItem
            linkTo="/member/payees"
            testId="collaborators"
            isVisible={focusedFeatures.showSideNavPayees}
            label={plural(terminology().sendPaymentsContractor)}
          />
          <SideNavMenuItem
            linkTo={ENGAGEMENT_TYPES_ROOT_PATH}
            testId="engagementTypes"
            isVisible={focusedFeatures.showSideNavEngagementRequirements}
            label="Engagement types"
          />
          <SideNavMenuItem
            linkTo={ENGAGEMENT_REQUIREMENTS_ROOT_PATH}
            testId="collaborators"
            isVisible={focusedFeatures.showSideNavEngagementRequirements}
            label="Engagement requirements"
          />
        </CollapsibleMenu>

        <CollapsibleMenu
          isVisible={focusedFeatures.showSideNavFinancialBenefits}
          isSideNavOpened={isSideNavOpened}
          icon="card"
          label="Financial benefits"
          isExpanded={expandedItems.includes(
            ExpandableSideNavMenuOptions.FinancialBenefits
          )}
          onClick={() =>
            onExpand(ExpandableSideNavMenuOptions.FinancialBenefits)
          }
        >
          <SideNavMenuItem
            linkTo="/member/wallet"
            testId="myFinancesWallet"
            isVisible
            label="Wingspan wallet"
          />

          <SideNavMenuItem
            linkTo="/member/bookkeeping"
            testId="myFinancesIncomeAndExpenses"
            isVisible={true}
            label="Income & expenses"
          />

          <SideNavMenuItem
            linkTo={PATH_TAXES}
            testId="myFinancesTaxWithholdings"
            isVisible={selectorCanEnableTaxWithholdings(
              queryMonitoredEntity.data,
              queryMember.data
            )}
            label="Tax withholdings"
          />
        </CollapsibleMenu>

        <CollapsibleMenu
          isVisible={focusedFeatures.showSideNavLifestyleBenefits}
          isSideNavOpened={isSideNavOpened}
          icon="heart"
          label="Lifestyle benefits"
          isExpanded={expandedItems.includes(
            ExpandableSideNavMenuOptions.LifestyleBenefits
          )}
          onClick={() =>
            onExpand(ExpandableSideNavMenuOptions.LifestyleBenefits)
          }
        >
          <SideNavMenuItem
            linkTo={PATH_BENEFITS_AXIS_INSURANCE}
            testId="benefits-axis-insurance"
            isVisible={focusedFeatures.showAxisBenefits}
            label="Axis insurance"
          />

          <SideNavMenuItem
            linkTo={PATH_BENEFITS_INSURANCE}
            testId="benefits-additional-coverage"
            isVisible={focusedFeatures.showBenefits}
            label="Insurance"
          />

          <SideNavMenuItem
            linkTo={PATH_BENEFITS_PERKS}
            testId="benefits-my-benefits"
            isVisible={focusedFeatures.showBenefits}
            label="Premium perks"
          />

          <SideNavMenuItem
            linkTo="/member/knowledge-center"
            testId="knowledgeCenter"
            isVisible={true}
            label="Knowledge center"
            exact={true}
          />
        </CollapsibleMenu>

        <SideNavRootLinkOption
          isVisible={focusedFeatures.showSideNavReports}
          to="/member/reports"
          testId="reports"
          label="Reporting"
          icon="report"
          isSideNavOpened={isSideNavOpened}
        />

        <CollapsibleMenu
          isVisible={focusedFeatures.showSideNavTaxFillingSection}
          isSideNavOpened={isSideNavOpened}
          icon="paperclip"
          label="Tax filing"
          isExpanded={expandedItems.includes(
            ExpandableSideNavMenuOptions.TaxFiling
          )}
          onClick={() => onExpand(ExpandableSideNavMenuOptions.TaxFiling)}
        >
          <SideNavMenuItem
            linkTo={TAX_FILING_1099_FILING_PATH}
            testId="NEC1099Filing"
            isVisible={true}
            label="1099 Filing"
          />
        </CollapsibleMenu>
      </WSFlexBox>
    </>
  );
};
